import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import type { Project, ProjectNdaSignature, User } from '../types';
import { storage } from './firebase';
import {
  getEffectiveProjectNdaSettings,
  getProjectNdaTextSections,
  STANDARD_PROJECT_NDA_TEMPLATE_VERSION,
} from '../utils/projectNda';

interface CreateNdaOptions {
  project: Project;
  organizationName: string;
  user: User;
  authUid?: string;
  googleDisplayName?: string | null;
  googleEmail?: string | null;
}

async function buildNdaPdf(
  sections: string[],
  signatureText: string,
  signedAt: string,
  projectName: string,
  organizationName: string
): Promise<Uint8Array> {
  const pdf = await PDFDocument.create();
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const boldFont = await pdf.embedFont(StandardFonts.HelveticaBold);

  const PAGE_W = 595;
  const PAGE_H = 842;
  const MARGIN = 50;
  const TEXT_W = PAGE_W - MARGIN * 2;
  const LINE_H = 15;

  let page = pdf.addPage([PAGE_W, PAGE_H]);
  let y = PAGE_H - MARGIN;

  const writeLine = (text: string, size = 9, useBold = false) => {
    if (y < MARGIN + LINE_H * 2) {
      page = pdf.addPage([PAGE_W, PAGE_H]);
      y = PAGE_H - MARGIN;
    }
    page.drawText(text, {
      x: MARGIN,
      y,
      size,
      font: useBold ? boldFont : font,
      color: rgb(0, 0, 0),
    });
    y -= LINE_H;
  };

  const writeWrapped = (text: string, size = 9) => {
    const words = text.split(' ');
    let line = '';
    for (const word of words) {
      const test = line ? `${line} ${word}` : word;
      if (font.widthOfTextAtSize(test, size) > TEXT_W && line) {
        writeLine(line, size);
        line = word;
      } else {
        line = test;
      }
    }
    if (line) writeLine(line, size);
  };

  writeLine(`PROJECT CONFIDENTIALITY AGREEMENT — ${projectName.toUpperCase()}`, 12, true);
  y -= LINE_H * 0.5;
  writeLine(`Organization: ${organizationName}`);
  writeLine(`Date signed: ${new Date(signedAt).toLocaleString()}`);
  y -= LINE_H;

  for (const section of sections) {
    writeWrapped(section);
    y -= LINE_H * 0.5;
  }

  y -= LINE_H;
  writeLine('Acknowledged and agreed by:');
  writeLine('___________________________________', 10);
  y -= LINE_H * 0.3;
  writeWrapped(signatureText);

  return pdf.save();
}

export async function createAndUploadProjectNdaSignature(
  options: CreateNdaOptions
): Promise<ProjectNdaSignature> {
  const { project, organizationName, user, authUid, googleDisplayName, googleEmail } = options;

  const signedAt = new Date().toISOString();
  const id = `nda-${project.id}-${(user.id || user.email).replace(/[^a-z0-9]/gi, '-')}-${Date.now()}`;
  const signerName = googleDisplayName || user.name;
  const signerEmail = googleEmail || user.email;
  const signatureText = `I, ${signerName} (${signerEmail}), have read and agree to the terms of this Non-Disclosure Agreement.`;

  const ndaSettings = getEffectiveProjectNdaSettings(project);
  const sections = getProjectNdaTextSections(project, organizationName);

  let pdfPath = '';
  let pdfUrl = '';

  try {
    const pdfBytes = await buildNdaPdf(
      sections,
      signatureText,
      signedAt,
      project.name,
      organizationName
    );

    const blob = new Blob([pdfBytes.buffer as ArrayBuffer], { type: 'application/pdf' });
    const storagePath = `organizations/${project.organizationId}/projects/${project.id}/nda/${signerEmail}/${Date.now()}.pdf`;
    const storageRef = ref(storage, storagePath);
    const snapshot = await uploadBytes(storageRef, blob, { contentType: 'application/pdf' });
    pdfUrl = await getDownloadURL(snapshot.ref);
    pdfPath = storagePath;
  } catch (err) {
    console.warn('[projectNdaService] PDF generation or upload failed — signature saved without PDF:', err);
  }

  return {
    id,
    organizationId: project.organizationId,
    projectId: project.id,
    userId: user.id,
    userEmail: signerEmail,
    userName: signerName,
    authUid,
    signedAt,
    signatureText,
    signatureProvider: 'google',
    documentVersion: ndaSettings.standardTemplateVersion || STANDARD_PROJECT_NDA_TEMPLATE_VERSION,
    pdfPath,
    pdfUrl,
  };
}
